import React, { useEffect, useState, } from 'react';
import OrderPayWithSwish from './OrderPayWithSwish';
import { scroller } from 'react-scroll';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  getAssociationBySlug,
  getMyUserQuery,
  createOrderMutation,
  updateOrderSupportAssociationMutation,
  getTeammemberBySlug,
  getTeamMembersSafeQuery,
  getBuyableBookletsQuery,
  getBookletQuery,
  getAssociationsQuery,
  getTeamBySlug,
  getVisibleAssociationsWithTeamsQuery
} from '../../queries/queries';
import { useHistory, useLocation } from 'react-router';
import PersonalWebshopsModal from './PersonalWebshopsModal';
import { useNavigationContext } from '../../context/NavigationContext';
import BookletCard from './BookletCard';
import ShareWebshop from './ShareWebshop';
import BookletView from '../vara-haften/BookletView';
import { GrClose } from 'react-icons/gr';
import { useAuth } from '../../context/AuthContext';
import { isWebshopAccessibleForOrder } from './utils/salesStatus';
import ShopHeader from './ShopHeader';
import Select from 'react-select';

function Order({ match = { params: {} } }) {
  const { params = {} } = match;
  const history = useHistory();
  const { state } = useLocation();
  const { isLoggedIn } = useAuth();
  const { setNavigationActivated } = useNavigationContext();

  const { data: userData } = useQuery(getMyUserQuery, { skip: !isLoggedIn });
  const { data: buyableBookletsData } = useQuery(getBuyableBookletsQuery);
  const { data: associationsData } = useQuery(getAssociationsQuery);

  // Fetch visible associations with teams from backend
  const domain = window.location.hostname;
  const { data: visibleAssociationsData } = useQuery(getVisibleAssociationsWithTeamsQuery, {
    variables: { domain },
    fetchPolicy: 'cache-and-network'
  });

  const { data: associationData, loading: associationLoading } = useQuery(getAssociationBySlug, {
    variables: { slug: params.association },
    skip: !params.association
  });
  const { data: teamData, loading: teamLoading } = useQuery(getTeamBySlug, {
    variables: {
      slug: params.team,
      associationSlug: params.association
    },
    skip: !params.team || !params.association
  });
  const { data: teamMemberData, loading: teamMemberLoading } = useQuery(getTeammemberBySlug, {
    variables: {
      slug: params.teammember,
      associationSlug: params.association,
      teamSlug: params.team
    },
    skip: !params.teammember || !params.association || !params.team
  });

  const [getPersonalWebshops, { data: teamMembersData }] = useLazyQuery(getTeamMembersSafeQuery);
  const [getBooklet, { data: bookletData }] = useLazyQuery(getBookletQuery);
  const [createOrder] = useMutation(createOrderMutation);
  const [updateOrderSupportAssociation] = useMutation(updateOrderSupportAssociationMutation);
  const [filteredBooklets, setFilteredBooklets] = useState();

  const [order, setOrder] = useState(null);
  const [webshopError, setWebshopError] = useState(null);
  const [bookletModal, setBookletModal] = useState({ open: false, booklet: null });
  const [selectedBooklet, setSelectedBooklet] = useState(null);
  const [showPersonalShops, setShowPersonalShops] = useState(false);
  const [isShareModal, setIsShareModal] = useState(false);
  const [selectedSupportAssociation, setSelectedSupportAssociation] = useState(null);

  const isLoading = associationLoading || teamLoading || teamMemberLoading;

  const supportAssociationOptions = associationData?.association?.supportableAssociations?.map(association => ({
    value: associationsData?.associations?.find(a => a._id === association)?._id,
    label: associationsData?.associations?.find(a => a._id === association)?.name
  }));

  const isMainAssociationShop = Boolean(
    associationData?.association?._id === selectedBooklet?.mainAssociationId
    && !teamData?.team && !teamMemberData?.teamMember);

  /**
   * Sales status logic:
   * - Webbshop is active only within date range
   * - For team shops: Always use team's values (even if null/undefined)
   * - For association shops: Use association's values
   */
  const isTeamShop = Boolean(teamData?.team);

  const salesStartDate = isTeamShop ? teamData?.team?.salesStartDate : associationData?.association?.salesStartDate;
  const salesEndDate = isTeamShop ? teamData?.team?.salesEndDate : associationData?.association?.salesEndDate;

  const isWebshopAccessible = isWebshopAccessibleForOrder(salesEndDate) && !webshopError && filteredBooklets?.length > 0;
  // Visa stöd-förenings-dropdown endast om man är i huvudföreningens shop för detta häfte och det finns stödföreningar att välja mellan
  const showSupportAssociationSelect = Boolean(
    isMainAssociationShop &&
    supportAssociationOptions?.length > 0
  );

  /**
   * Redirect to home page if sales are not active
   * This is to deactivate the webshop completely if sales are not active
   */
  // useEffect(() => {
  //   if (!isLoading && !isSalesActive) {
  //     history.push('/');
  //   }
  // }, [isLoading, isSalesActive, history]);

  useEffect(() => {
    setNavigationActivated(false);
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
  }, [history.location.pathname]);

  // Redirect to startpage if this association is the only visible one on this domain
  // The startpage will automatically show the order view for this association
  useEffect(() => {
    // Only check when we're actually on an association URL (not when rendered by Startpage)
    const currentPath = window.location.pathname;
    if (currentPath === '/' || currentPath === '') return;

    // Only check for association-only pages (not team or team member pages)
    if (!params.association || params.team || params.teammember) return;

    // Wait for all required data to load
    if (!associationData?.association || !visibleAssociationsData?.visibleAssociationsWithTeams) return;
    if (isLoading) return;

    const visibleAssociations = visibleAssociationsData.visibleAssociationsWithTeams;

    // If this association is the only visible one, check if it has active teams
    if (visibleAssociations.length === 1 && visibleAssociations[0]._id === associationData.association._id) {
      // Only redirect if there are NO active teams in this association
      if (visibleAssociations[0].teams.length === 0) {
        history.replace('/');
      }
    }
  }, [
    params.association,
    params.team,
    params.teammember,
    associationData,
    visibleAssociationsData,
    isLoading,
    history
  ]);

  // Set webshop error if no association/team/teammember is found
  useEffect(() => {
    if ((associationData && !associationData?.association) || (teamData && !teamData?.team) || (teamMemberData && !teamMemberData?.teamMember)) {
      setWebshopError(true);
    }
  }, [associationData, teamData, teamMemberData]);


  // Show personal webshops modal
  useEffect(() => {
    if (state?.isTeamSelected === undefined && teamData && !teamMemberData && !teamMemberLoading) {
      getPersonalWebshops({ variables: { teamId: teamData?.team?._id } });
      setShowPersonalShops(true);
    } else {
      setShowPersonalShops(false);
    }
  }, [teamData, teamMemberData]);


  useEffect(() => {
    const visibleBooklets = buyableBookletsData?.booklets?.filter(booklet => {
      // Show booklet if purchasableThroughAssociations includes the association id
      if (booklet?.purchasableThroughAssociations?.includes(associationData?.association?._id)) {
        return true;
      }
      return false;
    });

    setFilteredBooklets(visibleBooklets);

  }, [buyableBookletsData, associationData]);


  // Get booklet when modal is opened
  useEffect(() => {
    bookletModal.open && getBooklet({ variables: { _id: bookletModal.booklet._id } });
  }, [bookletModal]);


  // Get selected booklet and scroll down to payment section
  useEffect(() => {
    if (order) {
      getBooklet({ variables: { _id: order?.bookletId } });
      setTimeout(() => {
        scroller.scrollTo('payment-step', {
          duration: 800,
        });
      }, 400);
    }
  }, [order]);

  // Create order
  const handleSelectedBooklet = (booklet) => {
    setSelectedBooklet(booklet);

    // Om vi INTE är i huvudföreningens shop för detta häfte, sätt supportAssociationId till huvudföreningen
    let supportAssociationId = null;
    if (associationData?.association?._id !== booklet?.mainAssociationId) {
      supportAssociationId = booklet?.mainAssociationId || null;
    }

    const vars = {
      variables: {
        bookletId: booklet._id,
        associationId: fixOrderData(associationData?.association?._id),
        teamId: teamData?.team?._id ? fixOrderData(teamData.team?._id) : null,
        teamMemberId: teamMemberData?.teamMember?._id ? fixOrderData(teamMemberData.teamMember?._id) : null,
        ...(supportAssociationId && { supportAssociationId }),
      },
    };

    createOrder(vars)
      .then(result => setOrder(result && result.data && result.data.createOrder && result.data.createOrder))
      .catch(useMutationError => { console.log('regErr', useMutationError, useMutationError && useMutationError.graphQLErrors) });
  };

  function fixOrderData(Id) {
    //console.log('Id', Id, typeof Id, Id.length);
    if (Id && typeof Id === 'string' && Id.length == 24 && Id != '000000000000000000000000') {
      return Id;
    }
    return null;
  }

  const handleOpenShareModal = () => setIsShareModal(true);
  const handleCloseShareModal = () => setIsShareModal(false);


  const handleSupportAssociationChange = async (selected) => {
    const newSupportAssociationId = selected ? selected.value : null;
    setSelectedSupportAssociation(newSupportAssociationId);

    // Uppdatera ordern med den valda stödföreningen
    if (order?._id) {
      try {
        await updateOrderSupportAssociation({
          variables: {
            orderId: order._id,
            supportAssociationId: newSupportAssociationId
          }
        });
      } catch (error) {
        console.error('Kunde inte uppdatera stödförening:', error);
        // Återställ valet om det misslyckades
        setSelectedSupportAssociation(selectedSupportAssociation);
      }
    }
  };

  return (
    <div id="se-order">
      {!isLoading && (
        <>
          <ShopHeader
            teamMemberData={teamMemberData}
            teamData={teamData}
            associationData={associationData}
            salesStartDate={salesStartDate}
            salesEndDate={salesEndDate}
            webshopError={webshopError}
            handleOpenShareModal={handleOpenShareModal}
            bookletsLength={filteredBooklets?.length}
          />
          {isWebshopAccessible && (
            <>
              {/* HÄFTEN */}
              <div className="select-booklet">
                <h3>Välj häfte att köpa</h3>
                <div className="container">
                  {filteredBooklets?.map(booklet =>
                    <BookletCard
                      key={booklet._id}
                      booklet={booklet}
                      setBookletModal={setBookletModal}
                      selectedBooklet={selectedBooklet}
                      handleSelectedBooklet={handleSelectedBooklet}
                    />
                  )}
                </div>
              </div>

              {/* BETALNING */}
              {order &&
                <>
                  <div id="paymentStep" className="payment-step">
                    {showSupportAssociationSelect && (
                      <div className="select-support-association">
                        <span className="support">Jag vill stötta</span>
                        <Select
                          className={`react-select-container`}
                          classNamePrefix='react-select'
                          options={supportAssociationOptions}
                          placeholder={'Välj förening/stad'}
                          noOptionsMessage={() => 'Hittade ingen förening/stad'}
                          onChange={handleSupportAssociationChange}
                          value={supportAssociationOptions.find(opt => opt.value === selectedSupportAssociation) || null}
                          isClearable={true}
                        />
                      </div>
                    )}
                    <div className="container">
                      <OrderPayWithSwish
                        code={order?.code}
                        order={order}
                        orderId={order?._id}
                        email={userData?.user?.email}
                        association={associationData ? associationData?.association : null}
                        team={teamData ? teamData?.team?.name : null}
                        teamMember={teamMemberData ? `${teamMemberData?.teamMember?.firstname} ${teamMemberData?.teamMember?.lastname}` : null}
                        booklet={bookletData?.booklet}
                      />
                    </div>
                  </div>
                </>
              }

              <div className="contact-footer">
                <p>Mitt Häfte i Sverige AB</p>
                <p>Org.nr: 559469-8739</p>
                <p>Kullenvägen 27, 961 51 Boden</p>
                <p><a className='order-details-link' href="mailto:support@mitthafte.se">support@mitthafte.se</a></p>
              </div>


              {bookletModal.open &&
                <div className="show-booklet-modal modal">
                  <button className="close" onClick={() => setBookletModal({ open: false, booklet: null })}>
                    <GrClose color='black' />
                  </button>
                  <BookletView isInPopup match={{ params: { booklet: bookletModal.booklet._id } }} />
                </div>
              }
              {showPersonalShops && teamMembersData?.teamMembers?.length > 0 &&
                <PersonalWebshopsModal
                  teamMembers={teamMembersData?.teamMembers}
                  setShowPersonalShops={setShowPersonalShops}
                  association={associationData?.association}
                  team={teamData?.team}
                  teamSlug={params.team}
                />
              }
              {isShareModal &&
                <ShareWebshop
                  shopShareDomain={associationData?.association?.shopShareDomain || ''}
                  associationSlug={params.association}
                  teamSlug={params.team}
                  teamMemberSlug={params.teammember}
                  closeModal={handleCloseShareModal}
                />
              }
            </>
          )}
        </>
      )}
    </div>
  )
}

export default Order;
